import {
  Table,
  TabKeys,
  TableDividerStyle,
  TableTextStyle,
  ITEM_TYPES,
} from "../../../../types";
import {
  mapRelativeToPixels,
  resolveColorToBrandkitColorIfNeeded,
} from "../../../../helpers/itemsHelper";
import { Text } from "./Text";
import { ShapeRenderer } from "./shapes/ShapeRenderer";
import { DraggableComponent } from "./DraggableComponent";
import { AnimationPreviewWrapper } from "./AnimationPreviewWrapper";
import { useTemplateEditorStore } from "../../../../store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../../../../store/useTemplatesStatusStore";
import { useCallback, useState } from "react";
import { Input } from "antd";
import { useTemplateData } from "../../../../hooks/useTemplateData";
import { BORDER_COLOR } from "../../../../constants";

type TemplateTableProps = {
  table: Table;
  bulletPoints: string[];
  previewWidth: number;
  previewHeight: number;
  editable?: boolean;
};

const TableRow = ({
  content,
  textStyle,
  dividerStyle,
  cellWidth,
  cellHeight,
  previewWidth,
  previewHeight,
  editable,
  onContentChange,
  rowIndex,
  animationRowDelay,
  tableId,
  rotation,
}: {
  content: string;
  textStyle: TableTextStyle;
  dividerStyle?: TableDividerStyle;
  cellWidth: number;
  cellHeight: number;
  previewWidth: number;
  previewHeight: number;
  editable?: boolean;
  onContentChange?: (content: string) => void;
  rowIndex: number;
  animationRowDelay: number;
  tableId: string;
  rotation?: number;
}) => {
  // TODO: inline bullet point edition is to be fully implemented, this is only a starting point
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(content);

  const handleDoubleClick = useCallback(() => {
    if (!editable || !onContentChange) return;
    setEditValue(content);
    setIsEditing(true);
  }, [editable, content, onContentChange]);

  const handleFinishEditing = useCallback(() => {
    setIsEditing(false);
    onContentChange?.(editValue);
  }, [editValue, onContentChange]);

  const textItem = {
    ...textStyle,
    content,
    position: {
      x: textStyle.position.x,
      y: textStyle.position.y,
    },
    size: {
      width: (textStyle.size.width * cellWidth) / previewWidth,
      height:
        textStyle.size.height != null
          ? (textStyle.size.height * cellHeight) / previewHeight
          : null,
    },
    layerIndex: 1,
  };

  const textPixelWidth = textStyle.size.width * cellWidth;
  const textPixelHeight =
    textStyle.size.height != null
      ? textStyle.size.height * cellHeight
      : cellHeight;
  const textPixelX = textStyle.position.x * cellWidth;
  const textPixelY = textStyle.position.y * cellHeight;

  return (
    <div
      style={{
        position: "relative",
        width: cellWidth,
        height: cellHeight,
      }}
    >
      <div style={{ position: "absolute", inset: 0 }}>
        <AnimationPreviewWrapper
          animations={textStyle.animations}
          previewWidth={previewWidth}
          previewHeight={previewHeight}
          itemId={tableId}
          delay={rowIndex * animationRowDelay}
          rotation={rotation}
        >
          {isEditing ? (
            <div
              style={{
                position: "absolute",
                left: textPixelX,
                top: textPixelY,
                width: textPixelWidth,
                height: textPixelHeight,
                zIndex: 2,
              }}
            >
              <Input.TextArea
                autoFocus
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleFinishEditing}
                onPressEnter={handleFinishEditing}
                style={{
                  width: "100%",
                  height: "100%",
                  resize: "none",
                  border: `2px solid ${BORDER_COLOR}`,
                }}
              />
            </div>
          ) : (
            <div
              style={{
                position: "absolute",
                left: textPixelX,
                top: textPixelY,
              }}
              onDoubleClick={handleDoubleClick}
            >
              <Text
                textItem={textItem}
                previewWidth={previewWidth}
                previewHeight={previewHeight}
              />
            </div>
          )}
        </AnimationPreviewWrapper>
      </div>

      {dividerStyle && (
        <div style={{ position: "absolute", inset: 0 }}>
          <AnimationPreviewWrapper
            animations={dividerStyle.animations}
            previewWidth={cellWidth}
            previewHeight={cellHeight}
            itemId={tableId}
            delay={rowIndex * animationRowDelay}
            rotation={rotation}
          >
            <div
              style={{
                position: "absolute",
                left: dividerStyle.position.x * cellWidth,
                top: dividerStyle.position.y * cellHeight,
                width: dividerStyle.size.width * cellWidth,
                height: dividerStyle.size.height * cellHeight,
              }}
            >
              <ShapeRenderer
                shape={{
                  id: "divider",
                  shapeType: "line",
                  size: {
                    width: dividerStyle.size.width,
                    height: dividerStyle.size.height,
                  },
                  position: { x: 0, y: 0 },
                  background: dividerStyle.background,
                  layerIndex: 1,
                  orientation: dividerStyle.orientation,
                }}
                previewWidth={cellWidth}
                previewHeight={cellHeight}
              />
            </div>
          </AnimationPreviewWrapper>
        </div>
      )}
    </div>
  );
};

export const TemplateTable = ({
  table,
  bulletPoints,
  previewWidth,
  previewHeight,
  editable = false,
}: TemplateTableProps) => {
  const {
    brandKitData: { brandKitPalette },
  } = useTemplateData();
  const { updateTable } = useTemplateEditorStore((store) => ({
    updateTable: store.updateTable,
  }));
  const { addTemplateUpdate, setActiveTab, activeTab } =
    useTemplatesStatusStore((state) => ({
      addTemplateUpdate: state.addTemplateUpdate,
      setActiveTab: state.setActiveTab,
      activeTab: state.activeTab,
    }));

  const handleDragStop = useCallback(
    (update: Partial<Table>) => {
      updateTable(update);
      addTemplateUpdate();
    },
    [updateTable, addTemplateUpdate]
  );

  const rowCount = bulletPoints.length;
  if (rowCount === 0) return null;

  const tablePixelWidth = mapRelativeToPixels(table.size.width, previewWidth);
  const tablePixelHeight = mapRelativeToPixels(
    table.size.height,
    previewHeight
  );
  const cellHeight = tablePixelHeight / rowCount;

  const tableContent = (
    <div
      style={{
        width: tablePixelWidth,
        height: tablePixelHeight,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {table.background && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: resolveColorToBrandkitColorIfNeeded(
              table.background,
              brandKitPalette
            ),
          }}
        />
      )}
      {bulletPoints.map((content, index) => (
        <TableRow
          key={`${index}-${table.animationRowDelay ?? 0}`}
          content={content}
          textStyle={table.textStyle}
          dividerStyle={table.showDividers ? table.dividerStyle : undefined}
          cellWidth={tablePixelWidth}
          cellHeight={cellHeight}
          previewWidth={previewWidth}
          previewHeight={previewHeight}
          editable={editable}
          rowIndex={index}
          animationRowDelay={table.animationRowDelay ?? 0}
          tableId={table.id}
          rotation={editable ? table.rotation : undefined}
        />
      ))}
    </div>
  );

  if (editable) {
    return (
      <DraggableComponent
        position={table.position}
        size={table.size}
        rotation={table.rotation}
        layerIndex={table.layerIndex}
        onDragStop={handleDragStop}
        onRotate={(rotation) => updateTable({ rotation })}
        drag={true}
        resize={true}
        item={{ type: ITEM_TYPES.Table, id: table.id }}
        onFocus={() => {
          if (activeTab !== TabKeys.content && activeTab !== TabKeys.motion) {
            setActiveTab(TabKeys.content);
          }
        }}
        previewWidth={previewWidth}
        previewHeight={previewHeight}
      >
        {tableContent}
      </DraggableComponent>
    );
  }

  return (
    <div
      style={{
        position: "absolute",
        top: mapRelativeToPixels(table.position.y, previewHeight),
        left: mapRelativeToPixels(table.position.x, previewWidth),
        zIndex: table.layerIndex,
        transformOrigin: "center",
        transform: table.rotation ? `rotate(${table.rotation}deg)` : undefined,
      }}
    >
      {tableContent}
    </div>
  );
};
